import React, { useRef, useEffect } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    TimeScale,
    Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import moment from 'moment';
import 'chartjs-adapter-moment';
import pattern from 'patternomaly';
import _ from 'lodash';
import * as ChartLabels from './patient-chart/ChartLabels';
import ChartDataLabels from 'chartjs-plugin-datalabels';

const whiteBackgroundPlugin = {
    id: 'customCanvasBackgroundColor',
    beforeDraw: (chart) => {
      const {ctx} = chart;
      ctx.save();
      ctx.globalCompositeOperation = 'destination-over';
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, chart.width, chart.height);
      ctx.restore();
    },
    order: 999
};
  

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    TimeScale,
    Title,
    Tooltip,
    Legend,
    Filler,
    ChartDataLabels,
    whiteBackgroundPlugin  
);

const RespiratoryChart = ({ respirationData, visitTransitions, startTime, endTime, SettingResp, rangeOOOTransitions, trendsThresholds }) => {
    const chartRef = useRef(null);
    const outOfRangeLegendRef = useRef(null);

    // Draw the absence pattern for the legend
    useEffect(() => {
        if (outOfRangeLegendRef.current) {
            const ctx = outOfRangeLegendRef.current.getContext('2d');
            ctx.fillStyle = pattern.draw("diagonal-right-left", "#eceeee");
            ctx.fillRect(0, 0, 20, 20);
        }
    }, []);

    // Filter out zero values and format data for scatter plot
    const filteredData = (respirationData || [])
        .filter(point => point.y !== 0 && point.y !== 0.0)
        .map(point => ({
            t: point.t,
            y: point.y
        }));

    // Create absence segments for grayed out areas
    const absenceSegments = [];

    // Process visit transitions to create presence segments
    const formattedVisitData = (visitTransitions || [])
        .sort((a, b) => a.timestamp - b.timestamp)
        .map(t => ({
            timestamp: t.timestamp,
            isPresent: t.type === 'START'
        }));

    // Process transitions to create presence segments
    const presenceSegments = [];
    let currentSegmentStart = null;
    let currentPresent = false;
    
    // Initialize with the first state from visit data, or assume not present if no data
    if (formattedVisitData.length > 0) {
        currentPresent = formattedVisitData[0].isPresent;
        if (currentPresent) {
            currentSegmentStart = startTime;
        }
    }

    // If we have formatted visit data, process it into segments
    if (formattedVisitData.length > 0) {
        formattedVisitData.forEach((dataPoint) => {
            // Change in presence state
            if (dataPoint.isPresent !== currentPresent) {
                if (dataPoint.isPresent) {
                    // Starting presence
                    currentSegmentStart = dataPoint.timestamp;
                } else {
                    // Ending presence
                    if (currentSegmentStart) {
                        presenceSegments.push({
                            start: currentSegmentStart,
                            end: dataPoint.timestamp
                        });
                        currentSegmentStart = null;
                    }
                }
                currentPresent = dataPoint.isPresent;
            }
        });

        // Handle case where patient is present at the end of the time period
        if (currentPresent && currentSegmentStart) {
            presenceSegments.push({
                start: currentSegmentStart,
                end: endTime
            });
        }
    }

    // Create absence segments between presence segments
    if (presenceSegments.length === 0) {
        // No presence segments, so the entire time range is an absence
        absenceSegments.push({
            start: startTime,
            end: endTime
        });
    } else {
        // Create absence segments between presence segments
        let lastPresenceEnd = startTime;
        
        presenceSegments.forEach(segment => {
            if (segment.start > lastPresenceEnd) {
                absenceSegments.push({
                    start: lastPresenceEnd,
                    end: segment.start
                });
            }
            lastPresenceEnd = segment.end;
        });

        // Add absence segment at the end if needed
        if (lastPresenceEnd < endTime) {
            absenceSegments.push({
                start: lastPresenceEnd,
                end: endTime
            });
        }
    }
    
    // Create a function to identify segments where patient is present but no respiratory data exists
    const createNoDataSegments = () => {
        const noDataSegments = [];
        
        // Process each presence segment
        presenceSegments.forEach(presenceSegment => {
            // Find respiratory data within this presence segment
            const dataPointsInSegment = (respirationData || [])
                .filter(point => point.t >= presenceSegment.start && 
                       point.t <= presenceSegment.end && 
                       point.y !== 0 && point.y !== 0.0);
            
            if (dataPointsInSegment.length === 0) {
                // No data points in this entire presence segment
                noDataSegments.push({
                    start: presenceSegment.start,
                    end: presenceSegment.end
                });
            } else {
                // Sort data points by time
                const sortedPoints = [...dataPointsInSegment].sort((a, b) => a.t - b.t);
                
                // Check for gaps at the beginning of the presence segment
                if (sortedPoints[0].t > presenceSegment.start) {
                    noDataSegments.push({
                        start: presenceSegment.start,
                        end: sortedPoints[0].t
                    });
                }
                
                // Check for gaps between data points (if points are more than 30 minutes apart)
                for (let i = 0; i < sortedPoints.length - 1; i++) {
                    const currentPoint = sortedPoints[i];
                    const nextPoint = sortedPoints[i + 1];
                    const timeDiff = nextPoint.t - currentPoint.t;
                    
                    // If gap is more than 30 minutes (1800000 ms), consider it a no-data segment
                    if (timeDiff > 1800000) {
                        noDataSegments.push({
                            start: currentPoint.t,
                            end: nextPoint.t
                        });
                    }
                }
                
                // Check for gap at the end of the presence segment
                const lastPoint = sortedPoints[sortedPoints.length - 1];
                if (lastPoint.t < presenceSegment.end) {
                    noDataSegments.push({
                        start: lastPoint.t,
                        end: presenceSegment.end
                    });
                }
            }
        });
        
        return noDataSegments;
    };
    
    // Get the segments with no respiratory data
    const noDataSegments = createNoDataSegments();

    // Use thresholds from SettingResp if available, otherwise fall back to trends or calculated values
    let normalLow = 12; // Default fallback
    let normalHigh = 20; // Default fallback

    // First priority: Use SettingResp if available
    if (SettingResp && (SettingResp.normal_low !== undefined || SettingResp.normal_high !== undefined)) {
        normalLow = parseFloat(SettingResp.normal_low) || normalLow;
        normalHigh = parseFloat(SettingResp.normal_high) || normalHigh;
    } 
    // Second priority: Use trends thresholds if available
    else if (trendsThresholds?.respiratory) {
        normalLow = parseFloat(trendsThresholds.respiratory.normalLow) || normalLow;
        normalHigh = parseFloat(trendsThresholds.respiratory.normalHigh) || normalHigh;
    } 
    // Third priority: Calculate from data if available
    else if (respirationData?.length > 0) {
        const validRates = respirationData.filter(point => point.y > 0).map(point => point.y);
        if (validRates.length > 0) {
            const avgRate = validRates.reduce((sum, rate) => sum + rate, 0) / validRates.length;
            normalLow = Math.max(8, Math.round(avgRate * 0.8));
            normalHigh = Math.min(30, Math.round(avgRate * 1.2));
        }
    }

    // Log the final thresholds for debugging
    console.log('Respiratory normal range:', { normalLow, normalHigh, source: SettingResp ? 'SettingResp' : trendsThresholds ? 'trends' : 'calculated' });

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            intersect: false,
            mode: 'index'
        },
        parsing: {
            xAxisKey: 't',
            yAxisKey: 'y'
        },
        plugins: {
            customCanvasBackgroundColor: {
                color: 'white'
            },
            title: {
                display: true,
                text: 'Respiratory Rate',
                font: { size: 16 }
            },
            legend: {
                display: true,
                position: 'top',
                labels: {
                    filter: (item) => {
                        // Only show specific items in legend
                        return ['Normal Range', 'Respiratory Rate', 'Out of Range', 'Insufficient Data'].includes(item.text);
                    },
                    usePointStyle: true,
                    boxWidth: 10,
                    padding: 20
                },
                onHover: (event, legendItem, legend) => {
                    const tooltips = {
                        'Insufficient Data': 'Patient is present but no respiratory data is available',
                        'Out of Range': 'Patient is no longer in range of the Sensor',
                        'Normal Range': 'Expected normal respiratory rate',
                        'Respiratory Rate': 'Measured Breaths Per Minute (BPM)'
                    };
                    
                    // Change cursor to indicate interactivity
                    event.native.target.style.cursor = 'pointer';
                    
                    // Find tooltip container or create it if it doesn't exist
                    let tooltipEl = document.getElementById('chart-legend-tooltip');
                    if (!tooltipEl) {
                        tooltipEl = document.createElement('div');
                        tooltipEl.id = 'chart-legend-tooltip';
                        tooltipEl.style.position = 'absolute';
                        tooltipEl.style.background = 'rgba(0,0,0,0.7)';
                        tooltipEl.style.color = 'white';
                        tooltipEl.style.padding = '5px 10px';
                        tooltipEl.style.borderRadius = '3px';
                        tooltipEl.style.fontSize = '12px';
                        tooltipEl.style.pointerEvents = 'none';
                        tooltipEl.style.opacity = '0';
                        tooltipEl.style.transition = 'opacity 0.2s';
                        tooltipEl.style.zIndex = '10';
                        document.body.appendChild(tooltipEl);
                    }
                    
                    // Show tooltip with description
                    if (tooltips[legendItem.text]) {
                        tooltipEl.innerHTML = tooltips[legendItem.text];
                        tooltipEl.style.opacity = '1';
                        tooltipEl.style.left = event.native.pageX + 'px';
                        tooltipEl.style.top = (event.native.pageY - 28) + 'px'; // Position above the cursor
                    }
                },
                onLeave: (event, legendItem, legend) => {
                    // Reset cursor
                    if (event.native && event.native.target) {
                        event.native.target.style.cursor = 'default';
                    }
                    
                    // Hide tooltip
                    const tooltipEl = document.getElementById('chart-legend-tooltip');
                    if (tooltipEl) {
                        tooltipEl.style.opacity = '0';
                    }
                }
            },
            tooltip: {
                enabled: true,
                mode: 'index',
                intersect: false,
                callbacks: {
                    title: function(context) {
                        if (!context.length) return '';
                        return moment(context[0].parsed.x).format('MM/DD h:mma');
                    },
                    label: function(context) {
                        if (context.dataset.label === 'Respiratory Rate') {
                            return `Respiratory Rate: ${context.parsed.y.toFixed(1)} BPM`;
                        }
                        // Hide tooltips for all other datasets
                        return false; // Or return '' for an empty label
                    }
                }
            },
            datalabels: {
                display: false
            },
            filler: {
                propagate: false,
                drawTime: 'beforeDatasetsDraw'
            }
        },
        scales: {
            x: {
                type: 'time',
                time: {
                    unit: 'hour',
                    displayFormats: {
                        hour: 'MM/DD h:mma'
                    }
                },
                min: startTime,
                max: endTime,
                title: {
                    display: true,
                    text: 'Time'
                },
                ticks: {
                    maxRotation: 0,
                    autoSkip: true,
                    padding: 8,
                    align: 'center'
                },
                grid: {
                    color: 'rgba(0, 0, 0, 0.1)',
                    tickLength: 8,
                    offset: false
                },
                border: {
                    display: true
                },
                afterFit: function(scale) {
                    // Ensure consistent padding/margin across charts
                    scale.paddingRight = 15;
                    scale.paddingLeft = 15;
                }
            },
            y: {
                type: 'linear',
                min: 0,
                max: 30,
                title: {
                    display: true,
                    text: 'Breaths per Minute'
                },
                grid: {
                    color: 'rgba(0, 0, 0, 0.1)'
                }
            }
        }
    };

    const datasets = [];
    
    // Process and add the rangeOOOTransitions for out-of-range periods
    // Note: in rangeOOOTransitions, START means out-of-range begins, END means out-of-range ends
    const processedRangeTransitions = [];
    if (rangeOOOTransitions && rangeOOOTransitions.length > 0) {
        let startTransition = null;
        
        rangeOOOTransitions.forEach(transition => {
            if (transition.type === 'START') {
                startTransition = transition;
            } else if (transition.type === 'END' && startTransition) {
                // We have a complete START/END pair
                processedRangeTransitions.push({
                    start: startTransition,
                    end: transition
                });
                startTransition = null;
            }
        });
        
        // If we have a hanging START without an END, use the chart end time
        if (startTransition) {
            processedRangeTransitions.push({
                start: startTransition,
                end: { timestamp: endTime, type: 'END' }
            });
        }
    }
    
    // Create segmented no-data areas that respect both out-of-range periods and connected respiratory data lines
    const createRespectfulNoDataSegments = () => {
        if (!noDataSegments.length) {
            return []; // Return empty array if no no-data segments
        }
        
        // First, find all periods where respiratory lines are connected
        // Sort the filtered data chronologically
        const sortedRespData = [...filteredData].sort((a, b) => a.t - b.t);
        
        // Create pairs of connected respiratory data points
        const connectedPairs = [];
        for (let i = 0; i < sortedRespData.length - 1; i++) {
            const currentPoint = sortedRespData[i];
            const nextPoint = sortedRespData[i + 1];
            const timeDiff = nextPoint.t - currentPoint.t;
            
            // Determine if these points should be connected
            // Use the same logic as in createRespirationLineSegments
            // 1. Neither point is in an out-of-range period
            const currentInOutOfRange = processedRangeTransitions.some(
                range => currentPoint.t >= range.start.timestamp && currentPoint.t <= range.end.timestamp
            );
            const nextInOutOfRange = processedRangeTransitions.some(
                range => nextPoint.t >= range.start.timestamp && nextPoint.t <= range.end.timestamp
            );
            
            // 2. No out-of-range period between these points
            const hasInterveningRange = processedRangeTransitions.some(range => {
                return (range.start.timestamp > currentPoint.t && range.start.timestamp < nextPoint.t) ||
                       (range.end.timestamp > currentPoint.t && range.end.timestamp < nextPoint.t) ||
                       (range.start.timestamp <= currentPoint.t && range.end.timestamp >= nextPoint.t);
            });
            
            // If points should be connected, add the pair
            if (!currentInOutOfRange && !nextInOutOfRange && !hasInterveningRange) {
                connectedPairs.push({
                    start: currentPoint.t,
                    end: nextPoint.t
                });
            }
        }
        
        // Create segments for no-data that don't overlap with out-of-range periods or connected resp lines
        const refinedSegments = [];
        
        // For each no-data segment, process and refine it
        noDataSegments.forEach(noDataSegment => {
            // Check if this segment overlaps with any connected respiratory data pairs
            const isOverlappingConnectedPair = connectedPairs.some(pair => {
                // If the no-data segment overlaps with a connected pair, don't show it
                return (
                    // Connected pair completely contains no-data segment
                    (pair.start <= noDataSegment.start && pair.end >= noDataSegment.end) ||
                    // No-data segment starts within connected pair
                    (noDataSegment.start >= pair.start && noDataSegment.start <= pair.end) ||
                    // No-data segment ends within connected pair
                    (noDataSegment.end >= pair.start && noDataSegment.end <= pair.end)
                );
            });
            
            // Skip this segment if it overlaps with a connected respiratory data pair
            if (isOverlappingConnectedPair) {
                return; // Skip to next no-data segment
            }
            
            // Now check if it overlaps with any out-of-range period
            const overlappingRangeTransitions = processedRangeTransitions.filter(rangeTransition => {
                return (
                    // Range starts within no-data segment
                    (rangeTransition.start.timestamp >= noDataSegment.start && 
                     rangeTransition.start.timestamp <= noDataSegment.end) ||
                    // Range ends within no-data segment
                    (rangeTransition.end.timestamp >= noDataSegment.start && 
                     rangeTransition.end.timestamp <= noDataSegment.end) ||
                    // Range completely contains no-data segment
                    (rangeTransition.start.timestamp <= noDataSegment.start && 
                     rangeTransition.end.timestamp >= noDataSegment.end)
                );
            });
            
            if (overlappingRangeTransitions.length === 0) {
                // No overlap with out-of-range periods, include the original segment
                refinedSegments.push(noDataSegment);
            } else {
                // Handle overlaps with out-of-range periods
                // Sort overlapping ranges by start time
                const sortedRanges = [...overlappingRangeTransitions].sort(
                    (a, b) => a.start.timestamp - b.start.timestamp
                );
                
                let currentStart = noDataSegment.start;
                
                // Add segments between the ranges
                sortedRanges.forEach(range => {
                    // If range starts after current point, add a segment
                    if (range.start.timestamp > currentStart) {
                        refinedSegments.push({
                            start: currentStart,
                            end: range.start.timestamp
                        });
                    }
                    
                    // Move current point to end of this range
                    currentStart = range.end.timestamp > currentStart ? 
                        range.end.timestamp : currentStart;
                });
                
                // Add final segment if needed
                if (currentStart < noDataSegment.end) {
                    refinedSegments.push({
                        start: currentStart,
                        end: noDataSegment.end
                    });
                }
            }
        });
        
        return refinedSegments;
    };
    
    // Get the respectful no-data segments
    const respectfulNoDataSegments = createRespectfulNoDataSegments();
    
    // Create a simplified approach to segment respiratory data
    const createRespirationLineSegments = () => {
        // If no out-of-range transitions, return all data as one segment
        if (!processedRangeTransitions.length) {
            return [filteredData];
        }
        
        // Group data points into segments based on whether they're in an out-of-range period
        const segments = [];
        let currentSegment = [];
        
        // First sort the data chronologically
        const sortedData = [...filteredData].sort((a, b) => a.t - b.t);
        
        // For each data point, check if it falls within any out-of-range period
        sortedData.forEach(point => {
            // Check if this point is in any out-of-range period
            const isInOutOfRange = processedRangeTransitions.some(
                range => point.t >= range.start.timestamp && point.t <= range.end.timestamp
            );
            
            if (!isInOutOfRange) {
                // Check if we need to start a new segment by testing if there's any 
                // out-of-range period between this point and the previous one
                if (currentSegment.length > 0) {
                    const prevPoint = currentSegment[currentSegment.length - 1];
                    
                    // Check if any out-of-range period intersects with the line segment
                    // between the previous point and the current point
                    const hasIntervening = processedRangeTransitions.some(range => {
                        // If range starts after previous point and ends before current point
                        return (range.start.timestamp > prevPoint.t && range.start.timestamp < point.t) ||
                               (range.end.timestamp > prevPoint.t && range.end.timestamp < point.t) ||
                               // Or if range completely contains the segment between points
                               (range.start.timestamp <= prevPoint.t && range.end.timestamp >= point.t);
                    });
                    
                    if (hasIntervening) {
                        // There's an out-of-range period between these points
                        // End the current segment and start a new one
                        segments.push([...currentSegment]);
                        currentSegment = [];
                    }
                }
                
                // Add this point to the current segment
                currentSegment.push(point);
            } else {
                // This point is in an out-of-range period
                // If we have a segment in progress, end it
                if (currentSegment.length > 0) {
                    segments.push([...currentSegment]);
                    currentSegment = [];
                }
            }
        });
        
        // Add the final segment if it has any points
        if (currentSegment.length > 0) {
            segments.push(currentSegment);
        }
        
        return segments;
    };
    
    // Get respiration data segments
    const respirationLineSegments = createRespirationLineSegments();
    
    // Add scatter points (without connecting lines)
    datasets.push({
        label: 'Respiratory Rate',
        data: filteredData,
        backgroundColor: '#FFFFFF',  // Red color matching the line
        borderColor: '#004EFF',
        pointRadius: 3,
        pointHoverRadius: 5,
        showLine: false,  // Don't show connecting line
        yAxisID: 'y',
        order: 0  // Draw on top
    });
    
    // Add segmented line datasets (without points)
    respirationLineSegments.forEach((segment, index) => {
        if (segment.length > 0) {
            datasets.push({
                label: index === 0 ? 'Respiratory Rate Line' : `Respiratory Rate Line ${index}`,
                data: segment,
                borderColor: '#004EFF',
                backgroundColor: 'transparent',
                pointRadius: 0,  // Hide points
                pointHoverRadius: 0,
                showLine: true,
                tension: 0.4,
                fill: false,
                yAxisID: 'y',
                borderWidth: 2,
                order: 1  // Draw under points
            });
        }
    });
    
    // Create segmented normal range that doesn't appear during out-of-range periods
    const createNormalRangeSegments = () => {
        // If no out-of-range transitions, return entire time range as one segment
        if (!processedRangeTransitions.length) {
            return [{
                start: startTime,
                end: endTime
            }];
        }
        
        // Create segments for normal range by identifying time periods that are NOT out-of-range
        const segments = [];
        let lastEnd = startTime;
        
        // Sort transitions chronologically
        const sortedTransitions = [...processedRangeTransitions].sort((a, b) => 
            a.start.timestamp - b.start.timestamp
        );
        
        // For each out-of-range period, create a normal range segment before it
        sortedTransitions.forEach(range => {
            if (range.start.timestamp > lastEnd) {
                segments.push({
                    start: lastEnd,
                    end: range.start.timestamp
                });
            }
            lastEnd = range.end.timestamp;
        });
        
        // Add final segment after the last out-of-range period
        if (lastEnd < endTime) {
            segments.push({
                start: lastEnd,
                end: endTime
            });
        }
        
        return segments;
    };
    
    // Get normal range segments
    const normalRangeSegments = createNormalRangeSegments();
    
    // Add normal range segments as pairs of datasets with fill between
    normalRangeSegments.forEach((segment, index) => {
        // Add bottom line of normal range for this segment
        const bottomIndex = datasets.length;
        datasets.push({
            label: index === 0 ? 'Normal Range Bottom' : `Normal Range Bottom ${index}`,
            data: [
                { t: segment.start, y: normalLow },
                { t: segment.end, y: normalLow }
            ],
            borderColor: '#ADD8E6',
            pointRadius: 0,
            pointHoverRadius: 0,
            showLine: true,
            tension: 0,
            borderWidth: 1,
            order: 2  // Lower number = drawn later (foreground)
        });
        
        // Add top line with fill to the bottom line for this segment
        datasets.push({
            label: index === 0 ? 'Normal Range' : `Normal Range ${index}`,
            data: [
                { t: segment.start, y: normalHigh },
                { t: segment.end, y: normalHigh }
            ],
            borderColor: '#ADD8E6',
            backgroundColor: '#ADD8E6',
            fill: {
                target: bottomIndex  // Fill to the corresponding bottom line dataset
            },
            pointRadius: 0,
            pointHoverRadius: 0,
            showLine: true,
            tension: 0,
            borderWidth: 1,
            order: 2  // Lower number = drawn later (foreground)
        });
    });

    // Add out-of-range areas with gray color
    processedRangeTransitions.forEach((pair, index) => {
        datasets.push({
            data: [
                { t: pair.start.timestamp, y: 0 },
                { t: pair.start.timestamp, y: 30 },
                { t: pair.end.timestamp, y: 30 },
                { t: pair.end.timestamp, y: 0 }
            ],
            label: index === 0 ? 'Out of Range' : `Out of Range-${index}`,
            fill: true,
            borderColor: "rgba(0,0,0,0)",
            backgroundColor: "rgba(200, 200, 200, 0.5)", // Solid gray color for out-of-range
            pointRadius: 0,
            pointHoverRadius: 0,
            showLine: true,
            tension: 0,
            borderWidth: 0,
            order: 4  // Lower priority, drawn underneath
        });
    });
    
    // Add the no respiratory data segments with a distinct color (respecting out-of-range areas)
    respectfulNoDataSegments.forEach((segment, index) => {
        datasets.push({
            data: [
                { t: segment.start, y: 0 },
                { t: segment.start, y: 30 },
                { t: segment.end, y: 30 },
                { t: segment.end, y: 0 }
            ],
            label: index === 0 ? 'Insufficient Data' : `Insufficient Data-${index}`,
            fill: true,
            borderColor: "rgba(0,0,0,0)",
            backgroundColor: "rgba(128, 100, 162, 0.5)", // Purple color for present but no data
            pointRadius: 0,
            pointHoverRadius: 0,
            showLine: true,
            tension: 0,
            borderWidth: 0,
            order: 10  // Higher number = drawn earlier (background)
        });
    });

    // // Add absence segments with diagonal pattern
    // absenceSegments.forEach((segment, index) => {
    //     datasets.push({
    //         data: [
    //             { t: segment.start, y: 0 },
    //             { t: segment.start, y: 30 },
    //             { t: segment.end, y: 30 },
    //             { t: segment.end, y: 0 },
    //         ],
    //         label: `Patient Absent-${index}`,
    //         fill: true,
    //         borderColor: "rgba(0,0,0,0)",
    //         backgroundColor: "rgba(255, 255, 255, 0.5)", // Solid white/light gray for absence
    //         borderWidth: 0,
    //         pointRadius: 0,
    //         pointHoverRadius: 0,
    //         showLine: true,
    //         tension: 0,
    //         order: 5  // Lowest priority, drawn at the bottom
    //     });
    // });

    const data = {
        datasets
    };

    return (
        <div style={{ height: '400px', position: 'relative', backgroundColor: 'white' }}>
            <Line 
                ref={chartRef}
                options={options} 
                data={data} 
                style={{ backgroundColor: 'white' }}
            />
        </div>
    );
};

export default RespiratoryChart;
